import { Heading, Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { AirplaneLanding, AirplaneTakeoff } from '@phosphor-icons/react';
import { useDirection, useTranslation } from '@eti/providers';
import { Icon } from '@eti/components';
import { css } from '@eti/styles';
import type {
  BoundType,
  BuySpecificationCartTravelerChoiceType,
  BuySpecificationCartTravelerSeatMapType,
  ExtraProductType,
  TravelerType,
} from '../../../models';
import type { BoundBaggageInfoType } from '../baggages/models';
import BoundBaggageInfoPerTraveler from '../baggages/BoundBaggageInfoPerTraveler';
import BidirectionalSeparatedElements from '../../../../../common/bidirectional-component/BidirectionalSeparatedElements';
import ETickets from '../../common/ETickets';
import PassengerBoundSeat from './PassengerBoundSeat';
import PassengerBoundMeal from './PassengerBoundMeal';

const disclaimerStyles = css`
  color: #737373;
`;

const dividerStyles = css`
  border-bottom: 1px solid ${vars.colors.divider};
`;

const marginStyles = css`
  margin-inline-start: 26px;
`;

interface PassengerBoundInfoProps {
  bound: BoundType;
  boundBaggage: BoundBaggageInfoType;
  boundIndex: number;
  isReturnBound?: boolean;
  meal?: ExtraProductType | null;
  passenger: TravelerType;
  seat?: ExtraProductType | null;
}
const PassengerBoundInfo = ({
  bound,
  boundBaggage,
  boundIndex,
  isReturnBound,
  meal,
  passenger,
  seat,
}: PassengerBoundInfoProps) => {
  const { isRTL } = useDirection();
  const { t } = useTranslation();

  const passengerBoundBaggage = boundBaggage.baggageInfoPerTraveler.find(
    (travelerBaggageInfo) => travelerBaggageInfo.id === passenger.id,
  );

  const passengerBoundSeats = (
    seat?.buySpecification as BuySpecificationCartTravelerSeatMapType
  )?.travelerSeatMapPrices.filter(
    (seatProduct) =>
      seatProduct?.boundIndex === boundIndex && seatProduct?.travelerId === passenger.id,
  );

  const passengerBoundMeal = (
    meal?.buySpecification as BuySpecificationCartTravelerChoiceType
  )?.travelerChoicePrices.find((mealProduct) => mealProduct?.travelerId === passenger.id);

  return (
    <Stack>
      <Heading level={3}>
        <Inline spacing={8}>
          {isReturnBound ? (
            <AirplaneLanding
              aria-hidden="true"
              data-testid={`passenger-info-${bound.boundId}-landing-icon`}
              mirrored={isRTL}
              size={20}
            />
          ) : (
            <AirplaneTakeoff
              aria-hidden="true"
              data-testid={`passenger-info-${bound.boundId}-takeoff-icon`}
              mirrored={isRTL}
              size={20}
            />
          )}
          <BidirectionalSeparatedElements
            data-testid={`passenger-info-${bound.boundId}-origin-destination`}
            elements={[boundBaggage.origin, boundBaggage.destination]}
          />
        </Inline>
      </Heading>
      {passengerBoundBaggage && (
        <Stack spacing={4}>
          <Inline spacing={8}>
            <Icon data-testid="passenger-info-baggage-icon" id="checked-baggage-thin" size="20px" />
            <Heading data-testid="passenger-info-baggage-title" level={4}>
              {t('OrderDetails.Passenger.Drawer.Baggage.Title')}
            </Heading>
          </Inline>
          {passenger.id && (
            <BoundBaggageInfoPerTraveler
              className={marginStyles}
              dataTestId={`passenger-info--${bound.boundId}-traveler-${passenger.id}`}
              travelerBaggageInfo={passengerBoundBaggage}
            />
          )}
        </Stack>
      )}
      {passengerBoundSeats && (
        <PassengerBoundSeat
          bound={bound}
          passenger={passenger}
          passengerBoundSeats={passengerBoundSeats}
        />
      )}
      {passengerBoundMeal && <PassengerBoundMeal passengerBoundMeal={passengerBoundMeal} />}
      <Text className={disclaimerStyles} variant={textVariants._14}>
        {t('OrderDetails.CarrierUpdates.Disclaimer')}
      </Text>
      <div className={dividerStyles} />
      <Stack>
        <Heading level={4}>{t('OrderDetails.Passenger.Drawer.ETicket.Title')}</Heading>
        <ETickets bound={bound} passengerId={passenger.id} />
      </Stack>
    </Stack>
  );
};

export default PassengerBoundInfo;
